import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpParamsHelper } from '@api/http-params-helper';
import { DocumentTag } from '@api/types';
import { DocumentTagPaginator } from '@api/types/document_tag_paginator';
import { PaginatedGetParams } from '@shared/types/paginated-get-params';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class DocumentTagsService {
  constructor(private http: HttpClient) {}

  /**
   * Gets all the document tags for the given account.
   *
   * @summary `GET /accounts/{accountId}/document_tags`
   */
  get(accountId: string, params: PaginatedGetParams = { per_page: 20, page: 1 }): Observable<DocumentTagPaginator> {
    return this.http.get<DocumentTagPaginator>(`/accounts/${accountId}/document_tags`, {
      params: HttpParamsHelper.getHttpQueryParams(params)
    });
  }

  /**
   * Gets all DocumentTags for the Account
   *
   * @param accountId  The Id of the Account
   * @returns and Observable of  DocumentTags
   */
  getAllTags(accountId: string): Observable<DocumentTag[]> {
    return this.findAllTags(accountId);
  }

  /**
   * Creates a new document tag on the given account.
   *
   * @summary `POST /accounts/{account_id}/document_tags`
   */
  post(accountId: string, tag: Partial<DocumentTag>): Observable<DocumentTag> {
    return this.http.post<DocumentTag>(`/accounts/${accountId}/document_tags`, tag);
  }

  /**
   * Update the given document tag from the account.
   *
   * @summary `PATCH /accounts/{account_id}/notes/{note_id}`
   */
  patch(accountId: string, tagId: string, changes: Partial<DocumentTag>): Observable<DocumentTag> {
    return this.http.patch<DocumentTag>(`/accounts/${accountId}/document_tags/${tagId}`, changes);
  }

  /**
   * Deletes the given document tag from the account.
   *
   * @summary `DELETE /accounts/{account_id}/notes/{note_id}`
   */
  delete(accountId: string, tagId: string) {
    return this.http.delete(`/accounts/${accountId}/document_tags/${tagId}`);
  }

  /**
   * Accumulates all DocumentTags of the account using a recursive algorithm
   *
   */
  private findAllTags(
    accountId: string,
    tags: DocumentTag[] = [],
    params: PaginatedGetParams = { per_page: 20, page: 1 }
  ): Observable<DocumentTag[]> {
    return this.get(accountId, params).pipe(
      switchMap((response) => {
        const total = response.total || -1;
        const accumulatedTags = [...tags, ...response.data];

        if (accumulatedTags.length < total) {
          const newParams = { ...params, page: params.page + 1 };
          return this.findAllTags(accountId, accumulatedTags, newParams);
        }
        return of(accumulatedTags);
      })
    );
  }
}
